import { useCallback } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { db } from '@/js/external-js/config/main_config';
import { useAuth } from '@/contexts/AuthContext';

interface VersionSnapshot {
    version: string;
    timestamp: Date;
    userId: string;
    userEmail: string;
    changeType: string;
    changeDescription?: string;
    data: any;
}

/**
 * Bumps a semver string (major.minor.patch) for document version control.
 * Supported changeType values and their effects:
 *  - "delete" | "remove"       -> MAJOR bump (X+1.0.0)
 *  - "add" | "new"             -> MINOR bump (X.Y+1.0)
 *  - "edit" | "update"         -> PATCH bump (X.Y.Z+1, with cascading carry: 1.0.9 -> 1.1.0, 1.9.9 -> 2.0.0)
 *  - anything else / missing     -> PATCH bump (with cascading carry)
 */
const bumpSemver = (current: string, changeType?: string): string => {
    const [maj, min, pat] = (current || '0.0.0').split('.').map(n => {
        const x = Number(n);
        return Number.isFinite(x) ? x : 0;
    });

    let major = maj || 0;
    let minor = min || 0;
    let patch = pat || 0;
    const t = (changeType || 'update').toLowerCase();

    if (["delete", "remove"].includes(t)) {
        // Major bump: X+1.0.0
        major += 1;
        minor = 0;
        patch = 0;
    } else if (["add", "new"].includes(t)) {
        // Minor bump: X.Y+1.0
        minor += 1;
        patch = 0;
    } else {
        // "edit" | "update" | default -> Patch bump with cascading carry at 10
        patch += 1;
        if (patch >= 10) {
            patch = 0;
            minor += 1;
        }
        if (minor >= 10) {
            minor = 0;
            major += 1;
        }
    }

    return [major, minor, patch].join('.');
};

/**
 * Hook to manage MSG version updates with version control snapshots
 * Auto-increments following Semantic Versioning (major.minor.patch) for document changes:
 *  - changeType "delete"/"remove" -> major bump (resets minor & patch)
 *  - changeType "add"/"new" -> minor bump (resets patch)
 *  - changeType "edit"/"update" (or unspecified) -> patch bump (with cascading carry: 1.0.9 -> 1.1.0, 1.9.9 -> 2.0.0)
 */
export const useMSGVersion = () => {
    const { activeTenancy } = useTenancy();
    const { user } = useAuth();

    const incrementVersion = useCallback(async (changeType: string = 'content_update', changeDescription?: string, snapshotData?: any) => {
        if (!activeTenancy || !activeTenancy.id || ["", "Loading..."].includes(activeTenancy.id)) {
            console.warn('[useMSGVersion] No active tenancy, skipping version update');
            return;
        }

        try {
            const docRef = db.collection('customers')
                .doc(activeTenancy.id)
                .collection('product')
                .doc('managementSystemGuide');

            const docSnap = await docRef.get();
            
            if (docSnap.exists) {
                const data = docSnap.data();
                const currentVersion = data.version || '1.0.0';
                const newVersion = bumpSemver(currentVersion, changeType);
                const now = new Date();
                
                // Create snapshot for version control
                const snapshot: VersionSnapshot = {
                    version: newVersion,
                    timestamp: now,
                    userId: user?.uid || 'unknown',
                    userEmail: user?.email || 'unknown',
                    changeType,
                    changeDescription,
                    data: snapshotData || {}
                };
                
                // Save snapshot to version history subcollection
                await docRef.collection('versionHistory').add(snapshot);
                
                // Update main document
                await docRef.set({
                    version: newVersion,
                    lastUpdated: now
                }, { merge: true });
                
                console.log(`[useMSGVersion] Updated version from ${currentVersion} to ${newVersion}`, snapshot);
                
                return { version: newVersion, lastUpdated: now };
            } else {
                // Document doesn't exist, create with initial version
                const now = new Date();
                await docRef.set({
                    version: '1.0.0',
                    lastUpdated: now
                });
                
                console.log('[useMSGVersion] Created initial version 1.0.0');
                return { version: '1.0.0', lastUpdated: now };
            }
        } catch (error) {
            console.error('[useMSGVersion] Error updating version:', error);
            throw error;
        }
    }, [activeTenancy]);

    return { incrementVersion };
};
